import { Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DateDisplayType } from 'src/ca-shared/conversation-date-filter/models/date-display-type.enum';
import { GlobalSettingsService } from '../services/settings/global-settings.service';
import { parseFixedOffsetTimeZone } from '../helpers/timezone-offset-parser.helper';

@Pipe({
    name: 'caDatePipe',
    standalone: false
})
export class CADatePipe implements PipeTransform {
  constructor(private datePipe: DatePipe, private globalSettingsService: GlobalSettingsService) {}

  transform(
    value: any,
    dateType: DateDisplayType = DateDisplayType.DateTime,
    normalizeTimeZone: boolean = false
  ): any {
    if (value !== null) {
      const date = new Date(value);

      // Angular's native DatePipe/formatDate does not understand IANA zone ids: it resolves
      // the timezone via Date.parse('... ' + timezone), which returns NaN for ids like
      // "Europe/Berlin" and silently falls back to the browser's local offset. To support
      // IANA ids while still reusing Angular's format-token rendering (systemDateFormat,
      // 'HH:mm:ss', etc.), we pre-resolve the wall-clock time in the target zone and render
      // it via DatePipe with timezone 'UTC' so no further shifting occurs.
      var timeZone = normalizeTimeZone ? this.globalSettingsService.systemTimeZone : '';
      const zonedDate = timeZone ? this.toZonedFakeUtcDate(date, timeZone) : date;
      const effectiveTimeZone = timeZone ? 'UTC' : '';

      const dateString = this.datePipe.transform(
        zonedDate,
        this.globalSettingsService.systemDateFormat,
        effectiveTimeZone
      );
      const timeString = this.datePipe.transform(zonedDate, 'HH:mm:ss', effectiveTimeZone);
      const timeStringWithoutSeconds = this.datePipe.transform(zonedDate, 'HH:mm', effectiveTimeZone);
      const monthString  = this.datePipe.transform(zonedDate, 'MMM', effectiveTimeZone);

      if (dateType === DateDisplayType.DateTime) {
        return dateString + ' ' + timeString;
      } else if (dateType === DateDisplayType.DateTimeWithoutSeconds) {
        return dateString + ' ' + timeStringWithoutSeconds;
      } else if (dateType === DateDisplayType.Time) {
        return timeString;
      } else if (dateType === DateDisplayType.TimeWithoutSeconds) {
        return timeStringWithoutSeconds;
      } else if (dateType === DateDisplayType.Date) {
        return dateString;
      } else if (dateType === DateDisplayType.Month) {
        return monthString ;
      }
    } else {
      return '';
    }
  }

  /**
   * Resolves the wall-clock date/time for `timeZone` (fixed offset like "+0300" or IANA id
   * like "Europe/Berlin"; a fixed offset is computed arithmetically, an IANA id via
   * Intl.DateTimeFormat), then encodes those components into a Date
   * whose UTC fields equal that wall-clock time. Passing timezone 'UTC' to DatePipe
   * afterwards renders it verbatim, with no further offset shift.
   *
   * @param date Source instant to convert.
   * @param timeZone Fixed offset (e.g. "+0300") or IANA time zone id (e.g. "Europe/Berlin").
   * @returns A Date whose UTC fields hold the wall-clock time in `timeZone`.
   */
  private toZonedFakeUtcDate(date: Date, timeZone: string): Date {
    // Legacy fixed offsets (e.g. "+0330") are constant with no DST and are not reliably accepted
    // by Intl as a timeZone, so the wall-clock time is derived arithmetically.
    const fixedOffsetMinutes = parseFixedOffsetTimeZone(timeZone);

    if (fixedOffsetMinutes !== null) {
      return new Date(date.getTime() + fixedOffsetMinutes * 60000);
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    const parts = formatter.formatToParts(date).reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {} as Record<string, string>);

    const zonedDate = new Date(
      Date.UTC(
        Number(parts['year']),
        Number(parts['month']) - 1,
        Number(parts['day']),
        Number(parts['hour']),
        Number(parts['minute']),
        Number(parts['second'])
      )
    );

    return zonedDate;
  }
}
